const STORAGE_KEY = "image-lightness";

const cache = new Map<string, number>();

try {
  const raw: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
  if (typeof raw === "object" && raw !== null) {
    for (const [k, v] of Object.entries(raw)) {
      if (typeof v === "number") cache.set(k, v);
    }
  }
} catch {
  /* sessionStorage unavailable */
}

/**
 * Measure the average perceptual lightness (CIELAB L*) of the top strip
 * of an image. Loads a tiny 6x2 top-cropped thumbnail from the Shopify CDN.
 *
 * Returns L* from 0 (black) to 100 (white).
 */
export async function measureLightness(src: string): Promise<number> {
  const url = new URL(src, location.origin);
  url.searchParams.set("width", "6");
  url.searchParams.set("height", "2");
  url.searchParams.set("crop", "top");
  const key = url.href;

  const cached = cache.get(key);
  if (cached !== undefined) return cached;

  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = key;
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });

  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d canvas context unavailable");
  ctx.drawImage(img, 0, 0);
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);

  let totalL = 0;
  const pixelCount = data.length / 4;
  for (let i = 0; i < data.length; i += 4) {
    totalL += srgbToLstar(data[i], data[i + 1], data[i + 2]);
  }
  const lstar = totalL / pixelCount;

  cache.set(key, lstar);
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(cache)));
  } catch {
    /* sessionStorage full or unavailable */
  }
  return lstar;
}

/**
 * Convert sRGB (0-255) to CIELAB L* (0-100).
 */
export function srgbToLstar(r: number, g: number, b: number): number {
  const [rl, gl, bl] = [r / 255, g / 255, b / 255].map((c) =>
    c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4,
  );
  const y = 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
  return y <= 216 / 24389 ? y * (24389 / 27) : 116 * Math.cbrt(y) - 16;
}
